const BASE = process.env.REACT_APP_BACKEND_URL || "";

const TOKEN_KEY = "wa_token";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(t) {
  try {
    localStorage.setItem(TOKEN_KEY, t);
  } catch (e) {
    console.warn("Token opslaan mislukt (localStorage niet beschikbaar):", e);
  }
}
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    console.warn("Token verwijderen mislukt (localStorage niet beschikbaar):", e);
  }
}

// Turn any FastAPI/JSON error payload into a readable string.
// Prevents raw "[object Object]" from ever reaching the UI.
function normalizeDetail(detail, status) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (typeof d === "string" ? d : d && typeof d.msg === "string" ? d.msg : ""))
      .filter(Boolean);
    if (msgs.length) return msgs.join("\n");
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.error === "string") return detail.error;
  }
  return `Request failed (${status})`;
}

export async function api(path, options = {}) {
  const token = getToken();
  const headers = { "Content-Type": "application/json", "X-Client-Platform": "web", ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}/api${path}`, { ...options, headers });
  if (!res.ok) {
    let detail = "";
    let payload = null;
    try {
      payload = await res.json();
      detail = normalizeDetail(payload.detail ?? payload, res.status);
    } catch {
      try {
        detail = await res.text();
      } catch {
        detail = "";
      }
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export function isValidEmail(email) {
  return EMAIL_RE.test((email || "").trim());
}

export function formatPrice(p) {
  if (p === null || p === undefined) return "—";
  return `€${Number(p).toLocaleString("nl-NL")}`;
}

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function base64ToBlob(b64, mime = "application/octet-stream") {
  const byteChars = atob(b64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// Download a file from an authenticated backend endpoint.
export async function apiDownload(path, filename) {
  const token = getToken();
  const res = await fetch(`${BASE}/api${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download mislukt (${res.status})`);
  const blob = await res.blob();
  triggerDownload(blob, filename);
}

// Read a File into a base64 string (without the data: prefix).
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Compress an image File before upload — resizes to max `maxDim`px on the long
// edge, re-encodes as JPEG at `quality` (0..1). Returns a `data:` URL ready
// to store on the gallery. Keeps us under MongoDB's 16 MB BSON doc limit
// without needing object storage. Non-image files pass through as data URLs.
export async function compressImageFile(file, { maxDim = 1920, quality = 0.82 } = {}) {
  if (!file || !/^image\//.test(file.type)) {
    const b64 = await fileToBase64(file);
    return `data:${file?.type || "application/octet-stream"};base64,${b64}`;
  }
  const bitmap = await _loadBitmap(file);
  const { width: w0, height: h0 } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  if (bitmap.close) bitmap.close();
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  const dataUrl = await _blobToDataUrl(blob);
  return dataUrl;
}

// Re-compress an existing `data:image/...;base64,...` URL in place when it is
// larger than `maxBytes`. Used on save to shrink legacy oversized gallery
// entries so an admin can rescue a broken listing without re-uploading.
export async function shrinkDataUrlIfLarge(dataUrl, { maxBytes = 400 * 1024, maxDim = 1920, quality = 0.82 } = {}) {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) return dataUrl;
  // Rough byte count: base64 adds ~33%, so bytes ≈ (len - header) * 0.75.
  const commaAt = dataUrl.indexOf(",");
  const approxBytes = commaAt >= 0 ? Math.floor((dataUrl.length - commaAt - 1) * 0.75) : dataUrl.length;
  if (approxBytes <= maxBytes) return dataUrl;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    const bitmap = await _loadBitmap(blob);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const out = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return await _blobToDataUrl(out);
  } catch {
    return dataUrl;
  }
}

// Walk an array of data URLs and shrink any that exceed the per-image budget.
// Keeps non-string / url entries untouched (e.g. remote URLs already in object storage).
export async function shrinkGallery(gallery, opts) {
  if (!Array.isArray(gallery)) return gallery;
  return Promise.all(gallery.map((item) => shrinkDataUrlIfLarge(item, opts)));
}

function _loadBitmap(blob) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(blob);
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function _blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
